import { useState, useEffect, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Plus,
  Receipt,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AmountText,
  FinanceStatusBadge,
  TransactionTypeIcon,
  toneForTransactionType,
  type AmountTone
} from '@/components/finance-v2/FinanceBadges';
import { financeV2Service } from '../services/financeV2Service';
import { useFinanceViewMode } from '../contexts/FinanceViewModeContext';
import { QuickChargeModal } from '../components/finance-v2/simplified/QuickChargeModal';
import { parseDbTimestamp } from '../utils/datetime';
import { QuickReceiptModal } from '../components/finance-v2/simplified/QuickReceiptModal';
import { QuickExpenseModal } from '../components/finance-v2/simplified/QuickExpenseModal';
import type { Transaction } from '../types/financeV2.types';
import { financeSummaryService, type PeriodSummary } from '../services/financeSummaryService';

import { getErrorMessage } from '../utils/error.utils'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateString: string) =>
  parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) ?? '-';

/** Situação exibida no lançamento recente (vencido / a vencer / pago). */
function statusLabel(transaction: Transaction): string | null {
  if (transaction.status === 'PENDING' || transaction.status === 'OVERDUE') {
    return transaction.isOverdue || transaction.status === 'OVERDUE' ? 'Vencido' : 'A vencer';
  }
  return transaction.status === 'PAID' ? 'Pago' : null;
}

interface KpiCardProps {
  label: string;
  value: number;
  tone: AmountTone;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}

function KpiCard({ label, value, tone, icon: Icon, children }: KpiCardProps) {
  return (
    <Card size="sm">
      <CardContent className="flex h-full flex-col gap-2">
        <div className="flex items-center gap-3">
          <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="truncate text-xs text-muted-foreground">{label}</h3>
            <AmountText tone={tone} className="text-lg font-semibold">{formatCurrency(value)}</AmountText>
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export function FinanceV2SimplifiedPage() {
  const { toggleViewMode } = useFinanceViewMode();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<PeriodSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals state
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Totais do mês agregados no servidor sobre TODAS as linhas (antes eram
      // somados no navegador sobre as 10 primeiras); a lista mostra os recentes.
      const period = await financeSummaryService.period();
      const response = await financeV2Service.transactions.list({
        page: 1,
        limit: 10,
        dueDateFrom: period.from,
        dueDateTo: period.to
      });

      setSummary(period);
      setTransactions(response.data.transactions);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  };

  // KPIs do servidor (em aberto = a vencer + vencidos, pela regra única de vencido)
  const toReceive = summary?.toReceive.total ?? 0;
  const received = summary?.receivedInPeriod.total ?? 0;
  const toPay = summary?.toPay.total ?? 0;
  const overdueReceivable = summary?.overdue.receivable ?? { count: 0, total: 0 };

  // Get recent transactions (last 5)
  const recentTransactions = [...transactions]
    .sort((a, b) => {
      const bTime = parseDbTimestamp(b.dueDate)?.getTime() ?? 0;
      const aTime = parseDbTimestamp(a.dueDate)?.getTime() ?? 0;
      return bTime - aTime;
    })
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando financeiro...</span>
        <Skeleton className="h-8 w-64 max-w-full" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-28" />
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <DollarSign className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="min-w-0 break-words text-2xl font-semibold">Financeiro - Visão Rápida</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Gerencie suas cobranças e despesas de forma simples e rápida
          </p>
        </div>
        <Button variant="outline" onClick={toggleViewMode}>
          <Settings className="size-4" />
          Modo: Simplificado
          <span className="hidden text-xs text-muted-foreground sm:inline">(Alternar)</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="A Receber" value={toReceive} tone="neutral" icon={Clock}>
          <p className="text-xs text-muted-foreground">Em aberto com vencimento neste mês</p>
          {overdueReceivable.count > 0 && (
            <p className="flex items-start gap-1 text-xs text-destructive">
              <AlertCircle className="mt-px size-3 shrink-0" aria-hidden="true" />
              Vencidos (todos os meses): {formatCurrency(overdueReceivable.total)} em {overdueReceivable.count} lançamento(s)
            </p>
          )}
        </KpiCard>
        <KpiCard label="Recebido" value={received} tone="income" icon={CheckCircle}>
          <p className="text-xs text-muted-foreground">Neste mês</p>
        </KpiCard>
        <KpiCard label="A Pagar" value={toPay} tone="expense" icon={AlertCircle}>
          <p className="text-xs text-muted-foreground">Em aberto com vencimento neste mês</p>
        </KpiCard>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2>Ações Rápidas</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Button size="lg" className="h-11" onClick={() => setShowChargeModal(true)}>
            <FileText className="size-4" />
            Nova Cobrança
          </Button>
          <Button size="lg" variant="outline" className="h-11" onClick={() => setShowReceiptModal(true)}>
            <ArrowUpRight className="size-4 text-primary" />
            Recebimento
          </Button>
          <Button size="lg" variant="outline" className="h-11" onClick={() => setShowExpenseModal(true)}>
            <ArrowDownRight className="size-4 text-destructive" />
            Despesa
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2>Últimos Lançamentos</h2>
          </CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/finance-v2/transactions">Ver todos os lançamentos <ArrowRight className="size-3.5" /></Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentTransactions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Receipt className="size-10 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Nenhum lançamento encontrado neste mês</p>
            </div>
          ) : (
            <ul className="flex flex-col divide-y">
              {recentTransactions.map((transaction) => {
                const status = statusLabel(transaction);
                return (
                  <li
                    key={transaction.id}
                    className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <TransactionTypeIcon type={transaction.transactionType} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="wrap-break-word text-sm font-medium">{transaction.description}</p>
                          {status && <FinanceStatusBadge status={status} />}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Vencimento: {formatDate(transaction.dueDate)}
                        </p>
                      </div>
                    </div>
                    <AmountText
                      tone={toneForTransactionType(transaction.transactionType)}
                      className="pl-8 text-base font-semibold sm:pl-0"
                    >
                      {formatCurrency(transaction.amount)}
                    </AmountText>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <QuickChargeModal
        isOpen={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        onSuccess={loadData}
      />

      <QuickReceiptModal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        onSuccess={loadData}
      />

      <QuickExpenseModal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        onSuccess={loadData}
      />
    </div>
  );
}
